import i18n from '../i18n';

/**
 * 通用格式化工具
 */

/**
 * 将字节数格式化为可读字符串（B/KB/MB/GB/TB）
 * @param {number} bytes 字节数
 * @returns {string}
 */
export const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * GB 转字节
 * @param {number} gb
 * @returns {number}
 */
export const gbToBytes = (gb) => (gb ? gb * 1024 * 1024 * 1024 : 0);

/**
 * 字节转 GB
 * @param {number} bytes
 * @returns {number}
 */
export const bytesToGb = (bytes) => (bytes ? bytes / (1024 * 1024 * 1024) : 0);

/**
 * 将大数字格式化为紧凑写法（>=1,000,000 → M，>=1,000 → K）。
 * 用于 LLM 配额/用量等展示，替代 admin 与 user 端各自重复的 formatQuota/formatNumber。
 * @param {number} num
 * @returns {string}
 */
export const formatCompactNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toString();
};

/**
 * 回源地址归一化为字符串数组。
 * 兼容：数组、JSON 字符串 '["1.1.1.1"]'、历史遗留的 Python repr "['1.1.1.1', '1.2.2.22']"、
 * 逗号/换行分隔字符串、对象 {address|host|ip|origin, port?}。
 */
export const parseOrigins = (value) => {
    if (value === null || value === undefined || value === '') return [];
    const fromItem = (v) => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'object') {
            const host = v.address || v.host || v.ip || v.origin || v.url || '';
            if (!host) return null;
            return v.port ? `${host}:${v.port}` : String(host);
        }
        const s = String(v).trim().replace(/^['"]|['"]$/g, '');
        return s || null;
    };
    if (Array.isArray(value)) return value.map(fromItem).filter(Boolean);
    if (typeof value === 'object') return [fromItem(value)].filter(Boolean);
    const s = String(value).trim();
    if (s.startsWith('[') && s.endsWith(']')) {
        try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) return parsed.map(fromItem).filter(Boolean);
        } catch (_) {
            return s.slice(1, -1).split(',').map(fromItem).filter(Boolean);
        }
    }
    return s.split(/[,\n]/).map(fromItem).filter(Boolean);
};

/** 回源地址展示文本："1.1.1.1, 1.2.2.22" */
export const formatOrigins = (value, empty = '-') => {
    const list = parseOrigins(value);
    return list.length ? list.join(', ') : empty;
};

/**
 * "中文|English" 双语名称：按当前界面语言取一侧（中文界面取左，其它取右）。
 * @param {string} name
 * @param {string} [lang] 缺省取当前 i18n 语言
 */
export const localName = (name, lang) => {
    if (typeof name !== 'string' || !name.includes('|')) return name;
    const parts = name.split('|').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) return name;
    const cur = lang || i18n?.language || '';
    return /^zh/i.test(cur) ? parts[0] : parts[1];
};
